/**
 * What a list view shows in each of its two columns.
 *
 * A list view draws exactly two: a *member* and the *lists* it belongs to. Both
 * are usually link rows, and each may be displayed by a field of the linked
 * table other than its primary one -- the "target field" -- which the view's own
 * endpoint joins into the cell alongside the primary value.
 *
 * Pure, and here rather than in the view that draws it, because two things
 * need the same answer: the MCP view app, which renders a list view inside a
 * chat, and Phase B's list view when it is built.
 */
import type { Field, FieldKey, RowData } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import { valueText } from '../fields/read'

/**
 * A serialized cell value as a short label.
 *
 * Used only where the *field* is out of reach: a target field belongs to the
 * linked table, so it is not among the fields of the view's own table and its
 * type cannot be looked up. The shapes below are the ones a serialized value
 * comes in -- a select option or a link row is an object carrying a `value`, a
 * file carries a `url` -- and they are the same shapes the backend's own
 * `format_cell` handles when it writes this column for the agent, so both
 * audiences read the same label.
 *
 * Upstream reached for `String(...)` here, which renders every one of those as
 * `[object Object]`: fifteen characters that a reader takes for a value and a
 * `contains` filter can match.
 */
function labelText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) {
    return value
      .map(labelText)
      .filter((text) => text !== '')
      .join(', ')
  }
  if (typeof value === 'object') {
    const shape = value as { value?: unknown; url?: unknown }
    return labelText(shape.value ?? shape.url ?? null)
  }
  return ''
}

/**
 * The list view settings these functions read.
 *
 * Named with `_id` suffixes because they are ids, and because that is what the
 * MCP payload calls them -- it is built from Django's implicit foreign key
 * attributes. The REST API serialises the same four as `member_field`,
 * `list_field` and their targets, so an app reading a view from it converts
 * with `listSettings` rather than passing the view straight in. Two names for
 * one thing is a drift risk; converting in one named place is how it is
 * contained.
 */
export interface ListViewSettings {
  member_field_id?: number | null
  member_target_field_id?: number | null
  list_field_id?: number | null
  list_target_field_id?: number | null
  include_all_list_values?: boolean
  allowed_list_values?: readonly number[]
}

/** One entry of the list column: what it is called, and in what colour. */
export interface ListValue {
  id: number
  label: string
  color: string | null
}

/** A linked row as it arrives in a link-row cell, with any joined target field. */
interface LinkedValue {
  id: number
  value?: unknown
  color?: string | null
  [key: FieldKey]: unknown
}

function fieldWithId(
  fields: readonly Field[],
  fieldId: number | null | undefined,
): Field | null {
  if (fieldId === null || fieldId === undefined) return null
  return fields.find((field) => field.id === fieldId) ?? null
}

/**
 * A linked row as the column displays it.
 *
 * The target field when the join carried it, and the primary value otherwise --
 * a row that arrived through a realtime update has no join on it, and its own
 * name is a better answer than nothing.
 */
function linkedLabel(
  value: LinkedValue,
  targetFieldId: number | null | undefined,
): string {
  if (targetFieldId !== null && targetFieldId !== undefined) {
    const joined = value[`field_${targetFieldId}`]
    if (joined !== null && joined !== undefined) return labelText(joined)
  }
  return labelText(value.value)
}

/** Whatever a cell holds, as a list: a single select is a list of one. */
function asList(value: unknown): LinkedValue[] {
  if (value === null || value === undefined) return []
  return (Array.isArray(value) ? value : [value]) as LinkedValue[]
}

/** Whether both columns are configured, and the view can therefore show anything. */
export function isListViewConfigured(view: ListViewSettings): boolean {
  return (
    view.member_field_id !== null &&
    view.member_field_id !== undefined &&
    view.list_field_id !== null &&
    view.list_field_id !== undefined
  )
}

/**
 * The member column, as one string.
 *
 * A link row joins its linked rows with commas; anything else is rendered by
 * the same function every `contains` filter matches against, so the column says
 * what the grid would say.
 */
export function memberValue(
  row: RowData,
  view: ListViewSettings,
  fields: readonly Field[],
  context?: FieldContext,
): string {
  const field = fieldWithId(fields, view.member_field_id)
  if (field === null) return ''

  const value = row[`field_${field.id}`]
  if (value === null || value === undefined) return ''

  if (field.type === 'link_row') {
    return asList(value)
      .map((linked) => linkedLabel(linked, view.member_target_field_id))
      .filter((label) => label !== '')
      .join(', ')
  }

  return valueText(field.type, field, value, context)
}

/**
 * The list column's entries.
 *
 * The endpoint already leaves out rows that are in none of the allowed lists,
 * but a row that is in one of them can still hold others, and those are dropped
 * here rather than server-side: the row object is shared with the row modal and
 * with realtime updates, and has to stay complete.
 */
export function listValues(
  row: RowData,
  view: ListViewSettings,
  fields: readonly Field[],
): ListValue[] {
  const field = fieldWithId(fields, view.list_field_id)
  if (field === null) return []

  const isLinkRow = field.type === 'link_row'
  // Narrowed only when the view says so outright. The backend always sends the
  // flag and defaults it to true, so the two readings agree in practice; where
  // they differ -- a payload that omits it -- showing every value is the answer
  // that cannot silently empty a column.
  const allowed =
    view.include_all_list_values === false
      ? new Set(view.allowed_list_values ?? [])
      : null

  return asList(row[`field_${field.id}`])
    .filter((value) => allowed === null || allowed.has(value.id))
    .map((value) => ({
      id: value.id,
      label: isLinkRow
        ? linkedLabel(value, view.list_target_field_id)
        : labelText(value.value),
      color: value.color ?? null,
    }))
}

/** How the REST API names the same four columns, plus the two list rules. */
interface ListViewFromApi {
  member_field?: number | null
  member_target_field?: number | null
  list_field?: number | null
  list_target_field?: number | null
  include_all_list_values?: boolean
  allowed_list_values?: readonly number[]
}

/**
 * A list view as the API sends it, in the shape these functions read.
 *
 * The one place the two namings meet. Everything else -- the app's list view,
 * the MCP shell -- speaks `ListViewSettings`.
 */
export function listSettings(view: ListViewFromApi): ListViewSettings {
  return {
    member_field_id: view.member_field ?? null,
    member_target_field_id: view.member_target_field ?? null,
    list_field_id: view.list_field ?? null,
    list_target_field_id: view.list_target_field ?? null,
    // Spread rather than assigned, because `exactOptionalPropertyTypes` reads
    // "present and undefined" as different from absent -- and the difference
    // matters here: an absent flag means "show every value", which is what
    // `listValues` falls back to.
    ...(view.include_all_list_values === undefined
      ? {}
      : { include_all_list_values: view.include_all_list_values }),
    ...(view.allowed_list_values === undefined
      ? {}
      : { allowed_list_values: view.allowed_list_values }),
  }
}
